// The pending-change model of the master-data grid (DR-147). It lives in memory only: nothing is
// written to browser storage, and nothing reaches PostgreSQL until the user reviews and confirms.
// Existing units are identified only by `unitId` + `rowVersion`; a row's position on screen
// decides where pasted text lands, never which record it is.
import {
  type StructureListResponse,
  UNIT_CELL_FIELDS,
  UNIT_KIND_LABELS,
  UNIT_STATUS_LABELS,
  type UnitBatchResponse,
  type UnitBatchRow,
  type UnitBatchRowStatus,
  type UnitCellField,
  type UnitSummary,
  parseUnitCells,
} from '@sivan/shared';
import type { ClipboardTable } from './clipboard';

export type Cells = Record<UnitCellField, string>;
export type Structures = StructureListResponse['items'];

export const EMPTY_CELLS: Cells = Object.freeze(
  Object.fromEntries(UNIT_CELL_FIELDS.map((field) => [field, ''])) as Cells,
);

export interface GridRow {
  /** Stable key: the unit id for stored units, `new-<uuid>` for pending new rows. */
  readonly key: string;
  readonly unitId: string | null;
  /** The version the user's changes are based on. */
  readonly rowVersion: number | null;
  /** Stored values as cells; null for new rows. */
  readonly base: Cells | null;
  readonly cells: Cells;
  /** Field → error code, from local validation or the last server answer. */
  readonly errors: Readonly<Record<string, string>>;
  /** Classification from the last preview or rejected commit. */
  readonly outcome: UnitBatchRowStatus | null;
  /** For a version conflict: the version currently stored on the server. */
  readonly serverVersion: number | null;
}

export type RowState = 'stored' | 'changed' | 'new' | 'placeholder';

export function newRow(): GridRow {
  return {
    key: `new-${crypto.randomUUID()}`,
    unitId: null,
    rowVersion: null,
    base: null,
    cells: EMPTY_CELLS,
    errors: {},
    outcome: null,
    serverVersion: null,
  };
}

export function cellsFromUnit(unit: UnitSummary, structures: Structures): Cells {
  const structure = structures.find((s) => s.id === unit.structureId);
  const level = structure?.levels.find((l) => l.id === unit.levelId);
  return {
    code: unit.code,
    displayNumber: unit.displayNumber,
    kind: UNIT_KIND_LABELS[unit.kind],
    building: structure?.code ?? '',
    floor: level === undefined ? '' : String(level.number),
    modelType: unit.modelType ?? '',
    rooms: unit.rooms === null ? '' : String(unit.rooms),
    areaM2: unit.areaM2 === null ? '' : String(unit.areaM2),
    status: UNIT_STATUS_LABELS[unit.status],
  };
}

/** Stored units in server order, followed by the empty "new rows" placeholder. */
export function rowsFromUnits(units: readonly UnitSummary[], structures: Structures): GridRow[] {
  return [
    ...units.map((unit): GridRow => {
      const cells = cellsFromUnit(unit, structures);
      return {
        key: unit.id,
        unitId: unit.id,
        rowVersion: unit.rowVersion,
        base: cells,
        cells,
        errors: {},
        outcome: null,
        serverVersion: null,
      };
    }),
    newRow(),
  ];
}

export function changedFields(row: GridRow): UnitCellField[] {
  return UNIT_CELL_FIELDS.filter((field) =>
    row.base === null ? row.cells[field].trim() !== '' : row.cells[field] !== row.base[field],
  );
}

export function rowState(row: GridRow): RowState {
  if (row.unitId !== null) return changedFields(row).length > 0 ? 'changed' : 'stored';
  return changedFields(row).length > 0 ? 'new' : 'placeholder';
}

/** Immediate feedback with the same rules the server applies (it re-validates everything). */
export function validateRow(row: GridRow, structures: Structures): Record<string, string> {
  const fields = changedFields(row);
  if (fields.length === 0) return {};
  const given =
    row.base === null ? row.cells : Object.fromEntries(fields.map((f) => [f, row.cells[f]]));
  const { values, errors } = parseUnitCells(given, row.base === null ? 'create' : 'update');
  const result: Record<string, string> = { ...errors };
  const buildingCode = values.building ?? row.base?.building ?? row.cells.building;
  const structure =
    structures.find((s) => s.code === buildingCode) ??
    structures.find((s) => s.code.toLowerCase() === buildingCode.toLowerCase());
  if (values.building !== undefined && structure === undefined)
    result['building'] = 'unknown_building';
  if (
    structure !== undefined &&
    values.floor != null &&
    !structure.levels.some((l) => l.number === values.floor)
  ) {
    result['floor'] = 'unknown_floor';
  }
  return result;
}

/** Keeps exactly one empty placeholder row at the end, where new rows can be pasted or typed. */
function withPlaceholder(rows: GridRow[]): GridRow[] {
  const kept = rows.filter(
    (row, index) => rowState(row) !== 'placeholder' || index === rows.length - 1,
  );
  const last = kept.at(-1);
  return last !== undefined && rowState(last) === 'placeholder' ? kept : [...kept, newRow()];
}

function revalidated(row: GridRow, structures: Structures): GridRow {
  return { ...row, errors: validateRow(row, structures), outcome: null };
}

export function setCell(
  rows: readonly GridRow[],
  key: string,
  field: UnitCellField,
  value: string,
  structures: Structures,
): GridRow[] {
  return withPlaceholder(
    rows.map((row) =>
      row.key === key
        ? revalidated({ ...row, cells: { ...row.cells, [field]: value } }, structures)
        : row,
    ),
  );
}

export interface PasteResult {
  readonly rows: GridRow[];
  readonly updatedRows: number;
  readonly newRows: number;
  /** Pasted columns beyond the last field, which were not applied. */
  readonly ignoredColumns: number;
}

/**
 * Clipboard adapter, part 2 of 2: places a parsed range starting at the selected cell. Pasted row
 * i lands on the i-th displayed row below the anchor; rows beyond the display (or on the
 * placeholder) become pending new rows automatically. Pasted column j goes to the j-th field after
 * the anchor field, in logical order, which is right to left on screen in RTL. Blank cells change
 * nothing, and nothing is deleted.
 */
export function applyPaste(
  model: readonly GridRow[],
  displayed: readonly GridRow[],
  table: ClipboardTable,
  anchorIndex: number,
  anchorField: UnitCellField,
  structures: Structures,
): PasteResult {
  const firstField = UNIT_CELL_FIELDS.indexOf(anchorField);
  const width = Math.max(0, ...table.map((cells) => cells.length));
  const ignoredColumns = Math.max(0, firstField + width - UNIT_CELL_FIELDS.length);
  const updates = new Map<string, GridRow>();
  const created: GridRow[] = [];
  let updatedRows = 0;

  table.forEach((cells, i) => {
    const target = displayed[anchorIndex + i];
    const isNew = target === undefined || rowState(target) === 'placeholder';
    const base = target ?? newRow();
    const next: Cells = { ...base.cells };
    cells.forEach((value, j) => {
      // A blank pasted cell changes nothing (DR-152); clearing a value is an explicit edit.
      const field = UNIT_CELL_FIELDS[firstField + j];
      if (field !== undefined && value.trim() !== '') next[field] = value;
    });
    const row = revalidated({ ...base, cells: next }, structures);
    if (isNew) created.push(target === undefined ? row : { ...row, key: newRow().key });
    else {
      updates.set(row.key, row);
      if (row.unitId !== null) updatedRows += 1;
    }
  });

  const updatedModel = model.map((row) => updates.get(row.key) ?? row);
  const placeholderAt = updatedModel.findIndex((row) => rowState(row) === 'placeholder');
  const rows =
    placeholderAt === -1
      ? [...updatedModel, ...created]
      : [
          ...updatedModel.slice(0, placeholderAt),
          ...updatedModel.slice(placeholderAt + 1),
          ...created,
        ];
  return { rows: withPlaceholder(rows), updatedRows, newRows: created.length, ignoredColumns };
}

/** The batch to preview and commit: changed stored units (changed cells only) and new rows. */
export function toBatchRows(rows: readonly GridRow[]): UnitBatchRow[] {
  return rows.flatMap((row): UnitBatchRow[] => {
    const fields = changedFields(row);
    if (fields.length === 0) return [];
    if (row.unitId === null)
      return [{ op: 'create', clientRowId: row.key, cells: { ...row.cells } }];
    return [
      {
        op: 'update',
        clientRowId: row.key,
        unitId: row.unitId,
        rowVersion: row.rowVersion ?? 1,
        cells: Object.fromEntries(fields.map((f) => [f, row.cells[f]])),
      },
    ];
  });
}

/** Marks each row with the server's classification and field codes; keeps the draft intact. */
export function applyBatchResult(rows: readonly GridRow[], response: UnitBatchResponse): GridRow[] {
  const byKey = new Map(response.rows.map((result) => [result.clientRowId, result]));
  return rows.map((row) => {
    const result = byKey.get(row.key);
    if (result === undefined) return row;
    return {
      ...row,
      outcome: result.status,
      errors: result.fields,
      serverVersion: result.status === 'conflict' ? result.rowVersion : null,
    };
  });
}

/** Explicit choice after a version conflict: apply my change on top of the newer stored version. */
export function keepMyChange(rows: readonly GridRow[], key: string): GridRow[] {
  return rows.map((row) =>
    row.key === key && row.serverVersion !== null
      ? { ...row, rowVersion: row.serverVersion, serverVersion: null, outcome: null, errors: {} }
      : row,
  );
}

/** Drops the pending change of one row (a stored unit returns to its stored values). */
export function discardRow(rows: readonly GridRow[], key: string): GridRow[] {
  return withPlaceholder(
    rows.flatMap((row) => {
      if (row.key !== key) return [row];
      if (row.base === null) return [];
      return [{ ...row, cells: row.base, errors: {}, outcome: null, serverVersion: null }];
    }),
  );
}

export interface PendingSummary {
  readonly changed: number;
  readonly created: number;
  readonly withErrors: number;
}

export function summarize(rows: readonly GridRow[]): PendingSummary {
  let changed = 0;
  let created = 0;
  let withErrors = 0;
  for (const row of rows) {
    const state = rowState(row);
    if (state === 'changed') changed += 1;
    if (state === 'new') created += 1;
    if ((state === 'changed' || state === 'new') && Object.keys(row.errors).length > 0)
      withErrors += 1;
  }
  return { changed, created, withErrors };
}
